/**
 * 美国B1/B2签证材料与流程助手 - 业务逻辑工具函数库
 *
 * 功能：
 * 1. 领区判断 - 根据省份自动判定所属领事馆
 * 2. 免面谈判断 - 7条件严格验证（重点：上次签证签发时年龄≥18岁）
 * 3. EVUS判断 - 判断是否需要EVUS登记
 * 4. 材料清单生成 - 5组动态材料清单
 * 5. 流程步骤生成 - 10步流程（第6步根据免面谈动态调整）
 */

/**
 * 省份key到领区的映射表
 * 基于美国驻华使领馆官方管辖区域
 * 覆盖34个省级行政区
 */
export const PROVINCE_TO_DISTRICT = {
  // 北京领区（15个省级行政区）
  Beijing: '北京',
  Tianjin: '北京',
  Hebei: '北京',
  Shanxi: '北京',
  InnerMongolia: '北京',
  Shandong: '北京',
  Henan: '北京',
  Shaanxi: '北京',
  Gansu: '北京',
  Qinghai: '北京',
  Ningxia: '北京',
  Xinjiang: '北京',
  Tibet: '北京',

  // 上海领区（4个省级行政区）
  Shanghai: '上海',
  Jiangsu: '上海',
  Zhejiang: '上海',
  Anhui: '上海',

  // 广州领区（4个省级行政区）
  Guangdong: '广州',
  Guangxi: '广州',
  Fujian: '广州',
  Hainan: '广州',

  // 沈阳领区（3个省级行政区）
  Liaoning: '沈阳',
  Jilin: '沈阳',
  Heilongjiang: '沈阳',

  // 成都领区（4个省级行政区）
  Sichuan: '成都',
  Chongqing: '成都',
  Yunnan: '成都',
  Guizhou: '成都',

  // 武汉领区（4个省级行政区）
  Hubei: '武汉',
  Hunan: '武汉',
  Jiangxi: '武汉',

  // 🆕 港澳台地区（特别行政区和台湾地区）
  HongKong: '香港',  // 香港使馆
  Macao: '香港',     // 澳门 → 香港使馆
  Taiwan: '台北'     // 台湾 → 台北（AIT美国在台协会）
};

/**
 * 领区判断函数
 * @param {string} provinceKey - 省份key
 * @returns {string} 领区名称
 */
export function getConsularDistrict(provinceKey) {
  return PROVINCE_TO_DISTRICT[provinceKey] || '北京'; // 默认北京领区
}

/**
 * 免面谈判断函数 - 最关键的业务逻辑
 *
 * 必须同时满足7个条件才能符合免面谈资格
 * 参考：美国国务院领事事务局官网 B类续签免面谈政策（2024年更新）
 *
 * @param {object} inputs - 用户输入数据
 * @returns {boolean} 是否符合免面谈条件
 */
export function canWaiveInterview(inputs) {
  const {
    isFirstTime,
    lastVisaType,
    lastVisaFullValidity,
    lastVisaExpiredMonths,
    ageWhenLastIssued, // ⚠️ 关键字段：上次签证签发时年龄
    applyInResidenceCountry,
    everRefused
  } = inputs;

  // 条件1：必须是续签（非首次申请）
  if (isFirstTime) return false;

  // 条件2：上次签证必须是B1/B2类型
  const validVisaTypes = ['B1', 'B2', 'B1/B2'];
  if (!validVisaTypes.includes(lastVisaType)) return false;

  // 条件3：上次签证必须是"全有效期"（未被缩短）
  if (lastVisaFullValidity !== true) return false;

  // 条件4：必须在上次签证到期后12个月内提交续签申请
  const months = parseInt(lastVisaExpiredMonths, 10);
  if (isNaN(months) || months > 12) return false;

  // 条件5：⚠️ 最关键条件 - 上次签证签发时年龄必须≥18岁
  // 这是2024年后的新政策，不再按当前年龄自动免面谈
  const ageAtIssuance = parseInt(ageWhenLastIssued, 10);
  if (isNaN(ageAtIssuance) || ageAtIssuance < 18) return false;

  // 条件6：必须在国籍或常住地国家申请（非第三国）
  if (applyInResidenceCountry !== true) return false;

  // 条件7：从未被拒签过
  if (everRefused) return false;

  // 所有条件满足，符合免面谈条件
  return true;
}

/**
 * EVUS登记判断函数
 *
 * 仅当以下条件同时满足时，需要EVUS登记：
 * 1. 国籍为中国
 * 2. 持有10年有效期B1/B2签证
 *
 * @param {object} inputs - 用户输入数据
 * @returns {boolean} 是否需要EVUS登记
 */
export function needsEVUSCheck(inputs) {
  const { nationality, has10YearVisa } = inputs;
  return nationality === 'CN' && has10YearVisa === true;
}

/**
 * 材料清单生成函数 - 5组动态材料清单
 *
 * 根据用户情况动态生成材料清单：
 * G1: 必带材料（所有申请人）
 * G2: 支撑材料（B1/B2常见）
 * G3: 续签材料（仅续签且免面谈）
 * G4: 未成年人材料（仅<18岁）
 * G5: EVUS提醒（仅持有10年B签证）
 *
 * @param {object} profile - 用户信息profile
 * @returns {array} 材料组列表
 */
export function generateMaterialList(profile) {
  const { isFirstTime, age, canWaiveInterview: canWaive, needsEVUSCheck: needsEVUS } = profile;
  const groups = [];

  // G1: 必带材料（所有人）
  groups.push({
    id: 'G1',
    title: '必带材料 (Required Documents)',
    subtitle: '所有申请人都需要',
    color: 'blue',
    items: [
      { text: '有效护照原件（有效期需超过预计停留期至少6个月）', highlight: true },
      { text: 'DS-160确认页打印件（带条形码）', highlight: true },
      { text: '签证照片（5cm×5cm，白底，近6个月内）', highlight: false },
      { text: '预约确认信打印件', highlight: false },
      { text: '签证费收据（如适用）', highlight: false }
    ]
  });

  // G2: 支撑材料（B1/B2常见）
  groups.push({
    id: 'G2',
    title: '支撑材料 (Supporting Documents)',
    subtitle: 'B1/B2签证申请建议携带',
    color: 'green',
    items: [
      { text: '在职证明/在校证明/退休证明', highlight: false },
      { text: '近6个月银行流水或存款证明', highlight: true },
      { text: '房产证/车辆登记证等资产证明', highlight: false },
      { text: '行程计划/机票酒店预订单', highlight: false },
      { text: '邀请函（如探亲访友）', highlight: false },
      { text: '户口本复印件/结婚证', highlight: false }
    ]
  });

  // G3: 续签材料（仅续签且免面谈）
  if (!isFirstTime && canWaive) {
    groups.push({
      id: 'G3',
      title: '续签材料 (Renewal Documents)',
      subtitle: '免面谈续签专用',
      color: 'purple',
      items: [
        { text: '旧护照原件（含旧签证页）', highlight: true },
        { text: '旧护照个人信息页和签证页复印件', highlight: false }
      ]
    });
  }

  // G4: 未成年人材料（仅<18岁）
  if (age && parseInt(age, 10) < 18) {
    groups.push({
      id: 'G4',
      title: '未成年人材料 (Minor Documents)',
      subtitle: '18岁以下申请人必须提供',
      color: 'amber',
      items: [
        { text: '出生证明原件', highlight: true },
        { text: '父母双方身份证和护照复印件', highlight: false },
        { text: '父母双方同意函（如单独前往或只有一方陪同）', highlight: true },
        { text: '学校准假证明', highlight: false }
      ]
    });
  }

  // G5: EVUS提醒（仅适用）
  if (needsEVUS) {
    groups.push({
      id: 'G5',
      title: 'EVUS登记提醒 (EVUS Alert)',
      subtitle: '10年B类签证持有人必须完成',
      color: 'red',
      items: [
        {
          text: '🚨 您持有10年B1/B2签证，出发前必须完成EVUS登记，否则可能无法登机',
          highlight: true
        },
        {
          text: 'EVUS有效期通常为2年，护照或签证变更后需重新登记',
          highlight: false
        },
        { text: 'EVUS登记网址：evus.gov（英文）', highlight: false }
      ]
    });
  }

  return groups;
}

/**
 * 办理流程步骤生成函数 - 10步流程
 *
 * 流程第6步根据免面谈情况动态调整：
 * - 符合免面谈：递交材料到中信银行或快递至使馆
 * - 不符合免面谈：前往使领馆面谈
 *
 * @param {object} inputs - 用户输入数据
 * @returns {array} 流程步骤列表
 */
export function generateProcessSteps(inputs) {
  const { visaStatus, isFirstTime, lastVisaFullValidity, lastVisaExpiredMonths, ageWhenLastIssued, applyInResidenceCountry, everRefused } = inputs;

  // 🆕 场景1：已有有效签证，只需EVUS
  if (visaStatus === 'have_visa') {
    return [
      {
        step: 1,
        title: '确认签证有效期',
        desc: '检查您护照上的签证是否在有效期内，签证类型应为B1/B2',
        highlight: false
      },
      {
        step: 2,
        title: 'EVUS在线登记',
        desc: '完成EVUS在线登记，每2年或换护照后需重新登记',
        highlight: true,
        link: '/evus/enroll',
        linkText: '立即登记EVUS'
      },
      {
        step: 3,
        title: '确认EVUS状态',
        desc: '登记后确认EVUS状态为"enrolled"，保存确认信息',
        highlight: false,
        link: '/evus/query',
        linkText: '查询EVUS状态'
      }
    ];
  }

  // 场景2：需要申请签证（原有逻辑）
  const canWaive = !isFirstTime && canWaiveInterview(inputs);

  const steps = [
    {
      step: 1,
      title: '准备材料',
      desc: '根据材料清单准备护照、照片、资产证明等',
      highlight: false
    },
    {
      step: 2,
      title: '在线填写DS-160',
      desc: '使用旅报报平台在线填写DS-160申请表',
      highlight: false,
      link: '/home',
      linkText: '创建DS-160申请'
    },
    {
      step: 3,
      title: '缴纳签证费',
      desc: '根据领区选择缴费方式（中信银行等）',
      highlight: false
    },
    {
      step: 4,
      title: '预约面谈时间',
      desc: '在CGI官网预约系统选择可用的面谈时间',
      highlight: false,
      link: '/interview',
      linkText: '前往预约面谈'
    },
    {
      step: 5,
      title: '打印确认材料',
      desc: '打印DS-160确认页（带条形码）和预约确认信',
      highlight: false
    }
  ];

  // 第6步：根据免面谈情况动态变化
  if (canWaive) {
    steps.push({
      step: 6,
      title: '递交材料（免面谈）',
      desc: '将护照和材料送至指定中信银行网点，或快递至使馆（仍可能被要求面谈）',
      highlight: true,
      badge: '免面谈'
    });
  } else {
    steps.push({
      step: 6,
      title: '前往使领馆面谈',
      desc: '准时到达指定使领馆，携带所有材料，如实回答签证官问题',
      highlight: false
    });
  }

  // 后续步骤
  steps.push(
    {
      step: 7,
      title: '等待审理',
      desc: '通常需要1-3周，可能进行电话调查或行政审查',
      highlight: false
    },
    {
      step: 8,
      title: '护照返还',
      desc: '通过快递或到指定网点自取方式领取护照',
      highlight: false
    },
    {
      step: 9,
      title: '检查签证信息',
      desc: '核对签证类型、有效期、入境次数等信息是否正确',
      highlight: false,
      link: '/status',
      linkText: '查询签证状态'
    },
    {
      step: 10,
      title: 'EVUS登记（如需）',
      desc: '如果持有10年B1/B2签证，出发前务必完成EVUS登记',
      highlight: false,
      link: '/evus/enroll',
      linkText: '立即登记EVUS'
    }
  );

  return steps;
}

/**
 * 生成下一步行动清单
 * 根据用户当前状态给出3条以内的最重要下一步
 *
 * @param {object} profile - 用户信息profile
 * @returns {array} 下一步行动列表
 */
export function generateNextActions(profile) {
  const {
    province,
    isFirstTime,
    needsEVUSCheck: needsEVUS,
    canWaiveInterview: canWaive
  } = profile;

  const actions = [];

  // 行动1：根据领区确认申请地点
  if (province) {
    const district = getConsularDistrict(province);
    actions.push(`你属于 ${district}领事馆管辖区域，请在该领区进行申请`);
  }

  // 行动2：如果是首次申请，准备基础材料
  if (isFirstTime) {
    actions.push('首次申请需要面谈，请提前准备好所有材料和面谈相关内容');
  }

  // 行动3：如果需要EVUS，提前完成
  if (needsEVUS) {
    actions.push('⚠️ 如果持有10年B类签证，出发前必须完成EVUS登记，否则可能无法登机');
  }

  return actions.slice(0, 3); // 最多3条
}

/**
 * 验证用户输入的完整性
 * 用于判断是否有足够信息生成准确结果
 *
 * @param {object} inputs - 用户输入数据
 * @returns {object} 验证结果 {isValid: boolean, missingFields: array}
 */
export function validateUserInputs(inputs) {
  const missingFields = [];

  if (!inputs.age) missingFields.push('年龄');
  if (!inputs.province) missingFields.push('常住地省份');

  if (!inputs.isFirstTime) {
    if (!inputs.lastVisaType) missingFields.push('上次签证类型');
    if (inputs.lastVisaFullValidity === null) missingFields.push('上次是否全有效期');
    if (!inputs.lastVisaExpiredMonths) missingFields.push('上次签证到期月数');
    if (!inputs.ageWhenLastIssued) missingFields.push('上次签证签发时年龄');
    if (inputs.applyInResidenceCountry === null) missingFields.push('是否在居住国申请');
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}
